// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: Settings > Mobile rail. Picks up to five tabs for the phone rail and
// their order; Ask is always there. A live preview sits on top so the change
// is visible before the user leaves the page. Saved with the account.

import {SettingsSection} from '@app/features/app/components/dialogs/shared/SettingsSection';
import {SettingsTabContainer, SettingsTabContent} from '@app/features/app/components/dialogs/shared/SettingsTabLayout';
import {MobileRailBar} from '@app/features/app/components/layout/mobile_rail/MobileRailBar';
import {
	MOBILE_RAIL_ASK_LABEL,
	MOBILE_RAIL_TAB_LABELS,
	MobileRailTabIcon,
} from '@app/features/app/components/layout/mobile_rail/MobileRailTabLabels';
import {
	effectiveMobileMoreApps,
	MOBILE_MORE_MAX_APPS,
	MOBILE_RAIL_MAX_TABS,
	type MobileMoreAppId,
	type MobileRailTabId,
} from '@app/features/app/components/layout/mobile_rail/MobileRailTypes';
import {isAgentsEnabled} from '@app/features/app/components/layout/sidebar_nav/SidebarPagesLive';
import KonvoCalendarPrefs from '@app/features/konvo_calendar/state/KonvoCalendarPrefs';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import {CaretDownIcon, CaretUpIcon, DotsSixVerticalIcon, SparkleIcon} from '@app/features/ui/icons/Icons';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import styles from '@app/features/user/components/modals/tabs/KonvoMobileRailSettingsTab.module.css';
import {
	isMobileMoreFull,
	isMobileRailFull,
	mobileMoreAppEditorRows,
	mobileRailEditorRows,
	mobileRailFreeSlots,
	moveMobileMoreApp,
	moveMobileRailTab,
	toggleMobileMoreApp,
	toggleMobileRailTab,
} from '@app/features/user/components/modals/tabs/MobileRailEditorModel';
import {StartPageSection} from '@app/features/user/components/modals/tabs/StartPageSection';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useCallback, useEffect} from 'react';

const PREVIEW_DESCRIPTOR = msg({
	message: 'Preview',
	comment: 'Caption above the live preview of the phone rail in Settings > Mobile rail.',
});
const RAIL_IS_FULL_DESCRIPTOR = msg({
	message: 'Rail is full',
	comment: 'Hint on a disabled row in Settings > Mobile rail when five tabs are already chosen.',
});
const ALWAYS_ON_THE_RAIL_DESCRIPTOR = msg({
	message: 'Always on the rail',
	comment: 'Note on the locked Ask row in Settings > Mobile rail.',
});
const MOVE_UP_DESCRIPTOR = msg({
	message: 'Move {label} up',
	comment: 'Accessible label for the button that moves a rail tab one place earlier.',
});
const MOVE_DOWN_DESCRIPTOR = msg({
	message: 'Move {label} down',
	comment: 'Accessible label for the button that moves a rail tab one place later.',
});
const SHOW_ON_RAIL_DESCRIPTOR = msg({
	message: 'Show {label} on the rail',
	comment: 'Accessible label for the toggle that adds or removes a tab from the phone rail.',
});
const MORE_IS_FULL_DESCRIPTOR = msg({
	message: 'More is full',
	comment: 'Hint on a disabled More-app row when four quick-menu slots are already chosen.',
});
const ON_THE_RAIL_DESCRIPTOR = msg({
	message: 'On the rail',
	comment: 'Hint in Settings > Mobile rail when an app is already directly on the rail.',
});
const SHOW_IN_MORE_DESCRIPTOR = msg({
	message: 'Show {label} in More',
	comment: 'Accessible label for the toggle that adds or removes an app from the phone rail More menu.',
});
const MOVE_MORE_UP_DESCRIPTOR = msg({
	message: 'Move {label} earlier in More',
	comment: 'Accessible label for reordering a More-menu app.',
});
const MOVE_MORE_DOWN_DESCRIPTOR = msg({
	message: 'Move {label} later in More',
	comment: 'Accessible label for reordering a More-menu app.',
});

const CountSegments = ({filled, total = MOBILE_RAIL_MAX_TABS}: {filled: number; total?: number}) => (
	<div className={styles.countBar} aria-hidden="true" data-flx="user.konvo-mobile-rail-tab.count-bar">
		{Array.from({length: total}, (_, index) => (
			<i
				key={index}
				className={clsx(styles.countSegment, index < filled && styles.countSegmentFilled)}
				data-flx="user.konvo-mobile-rail-tab.count-segment"
			/>
		))}
	</div>
);

export const KonvoMobileRailSettingsTab = observer(() => {
	const {i18n} = useLingui();
	useEffect(() => {
		void KonvoCalendarPrefs.load();
	}, []);
	// KONVO: Agents is only offered to people with agents access; More starts from what the panel shows.
	const agents = isAgentsEnabled();
	const tabs = KonvoCalendarPrefs.mobileRailTabs;
	const moreApps = effectiveMobileMoreApps(KonvoCalendarPrefs.mobileMoreApps, tabs, agents);
	const full = isMobileRailFull(tabs);
	const free = mobileRailFreeSlots(tabs);
	const rows = mobileRailEditorRows(tabs).filter((row) => agents || row.id !== 'agents');
	const moreFull = isMobileMoreFull(moreApps);
	const moreRows = mobileMoreAppEditorRows(moreApps, tabs).filter((row) => agents || row.id !== 'agents');
	const isPhone = MobileLayout.enabled;

	const commitRail = useCallback(
		(next: Array<MobileRailTabId>) => {
			const nextMore = moreApps.filter((id) => !next.includes(id));
			void KonvoCalendarPrefs.save({mobileRailTabs: next, mobileMoreApps: nextMore});
		},
		[moreApps],
	);
	const commitMore = useCallback((next: Array<MobileMoreAppId>) => {
		void KonvoCalendarPrefs.save({mobileMoreApps: next});
	}, []);
	const handleToggle = useCallback(
		(id: MobileRailTabId) => commitRail(toggleMobileRailTab(tabs, id)),
		[commitRail, tabs],
	);
	const handleMove = useCallback(
		(id: MobileRailTabId, direction: -1 | 1) => commitRail(moveMobileRailTab(tabs, id, direction)),
		[commitRail, tabs],
	);
	const handleMoreToggle = useCallback(
		(id: MobileMoreAppId) => commitMore(toggleMobileMoreApp(moreApps, tabs, id)),
		[commitMore, moreApps, tabs],
	);
	const handleMoreMove = useCallback(
		(id: MobileMoreAppId, direction: -1 | 1) => commitMore(moveMobileMoreApp(moreApps, id, direction)),
		[commitMore, moreApps],
	);

	return (
		<SettingsTabContainer data-flx="user.konvo-mobile-rail-tab.container">
			<SettingsTabContent data-flx="user.konvo-mobile-rail-tab.content">
				<SettingsSection
					id="konvo-mobile-rail-tabs"
					title={<Trans>Rail tabs</Trans>}
					linkable={false}
					className={styles.section}
					data-flx="user.konvo-mobile-rail-tab.section"
				>
					<div className={styles.editor} data-flx="user.konvo-mobile-rail-tab.editor">
						{!isPhone && (
							<p className={styles.note} data-flx="user.konvo-mobile-rail-tab.desktop-note">
								<Trans>These tabs make up the rail at the bottom of the phone app. Changes apply there.</Trans>
							</p>
						)}
						<div className={styles.preview} data-flx="user.konvo-mobile-rail-tab.preview">
							<span className={styles.previewCaption} data-flx="user.konvo-mobile-rail-tab.preview-caption">
								{i18n._(PREVIEW_DESCRIPTOR)}
							</span>
							<div className={styles.previewBar} data-flx="user.konvo-mobile-rail-tab.preview-bar">
								<MobileRailBar
									tabs={tabs}
									activeTab={tabs[0] ?? null}
									preview
									data-flx="user.konvo-mobile-rail-tab.preview-rail"
								/>
							</div>
						</div>
						<div className={styles.count} data-flx="user.konvo-mobile-rail-tab.count">
							<span data-flx="user.konvo-mobile-rail-tab.count-text">
								{full ? (
									<Trans>
										<b>
											{tabs.length} of {MOBILE_RAIL_MAX_TABS}
										</b>{' '}
										on the rail · rail is full
									</Trans>
								) : (
									<Trans>
										<b>
											{tabs.length} of {MOBILE_RAIL_MAX_TABS}
										</b>{' '}
										on the rail · {free} free
									</Trans>
								)}
							</span>
							<CountSegments filled={tabs.length} data-flx="user.konvo-mobile-rail-tab.count-segments" />
						</div>
						<div className={styles.rows} data-flx="user.konvo-mobile-rail-tab.rows">
							<div className={clsx(styles.row, styles.rowLocked)} data-flx="user.konvo-mobile-rail-tab.row-ask">
								<span className={styles.grab} aria-hidden="true" data-flx="user.konvo-mobile-rail-tab.grab-ask">
									<DotsSixVerticalIcon size={18} data-flx="user.konvo-mobile-rail-tab.grab-icon-ask" />
								</span>
								<span className={styles.iconTile} data-flx="user.konvo-mobile-rail-tab.icon-tile-ask">
									<SparkleIcon size={18} data-flx="user.konvo-mobile-rail-tab.icon-ask" />
								</span>
								<span className={styles.label} data-flx="user.konvo-mobile-rail-tab.label-ask">
									{i18n._(MOBILE_RAIL_ASK_LABEL)}
								</span>
								<span className={styles.lockNote} data-flx="user.konvo-mobile-rail-tab.lock-note">
									{i18n._(ALWAYS_ON_THE_RAIL_DESCRIPTOR)}
								</span>
							</div>
							{rows.map((row) => {
								const label = i18n._(MOBILE_RAIL_TAB_LABELS[row.id]);
								const faded = !row.on && full;
								return (
									<div
										key={row.id}
										className={clsx(styles.row, !row.on && styles.rowOff, faded && styles.rowFull)}
										data-flx="user.konvo-mobile-rail-tab.row"
									>
										<span className={styles.grab} aria-hidden="true" data-flx="user.konvo-mobile-rail-tab.grab">
											<DotsSixVerticalIcon size={18} data-flx="user.konvo-mobile-rail-tab.grab-icon" />
										</span>
										<span className={styles.iconTile} data-flx="user.konvo-mobile-rail-tab.icon-tile">
											<MobileRailTabIcon tab={row.id} size={18} data-flx="user.konvo-mobile-rail-tab.icon" />
										</span>
										<span className={styles.label} data-flx="user.konvo-mobile-rail-tab.label">
											{label}
											{faded && (
												<small className={styles.labelHint} data-flx="user.konvo-mobile-rail-tab.label-hint">
													{i18n._(RAIL_IS_FULL_DESCRIPTOR)}
												</small>
											)}
										</span>
										{row.on && (
											<span className={styles.moveButtons} data-flx="user.konvo-mobile-rail-tab.move-buttons">
												<button
													type="button"
													className={styles.moveButton}
													disabled={!row.canMoveUp}
													onClick={() => handleMove(row.id, -1)}
													aria-label={i18n._(MOVE_UP_DESCRIPTOR, {label})}
													data-flx="user.konvo-mobile-rail-tab.move-up"
												>
													<CaretUpIcon size={16} data-flx="user.konvo-mobile-rail-tab.move-up-icon" />
												</button>
												<button
													type="button"
													className={styles.moveButton}
													disabled={!row.canMoveDown}
													onClick={() => handleMove(row.id, 1)}
													aria-label={i18n._(MOVE_DOWN_DESCRIPTOR, {label})}
													data-flx="user.konvo-mobile-rail-tab.move-down"
												>
													<CaretDownIcon size={16} data-flx="user.konvo-mobile-rail-tab.move-down-icon" />
												</button>
											</span>
										)}
										<Switch
											compact
											value={row.on}
											disabled={!row.canToggle}
											onChange={() => handleToggle(row.id)}
											ariaLabel={i18n._(SHOW_ON_RAIL_DESCRIPTOR, {label})}
											className={styles.switch}
											data-flx="user.konvo-mobile-rail-tab.switch"
										/>
									</div>
								);
							})}
						</div>
						<p className={styles.hint} data-flx="user.konvo-mobile-rail-tab.hint">
							<Trans>Ask stays on the rail. If you remove More, hold Ask to open the panel.</Trans>
						</p>
					</div>
				</SettingsSection>
				{isPhone && <StartPageSection formFactor="phone" data-flx="user.konvo-mobile-rail-tab.start-page" />}
				<SettingsSection
					id="konvo-mobile-more-apps"
					title={<Trans>More apps</Trans>}
					description={
						<Trans>Choose up to four quick shortcuts. An app can appear on the rail or in More, not both.</Trans>
					}
					linkable={false}
					data-flx="user.konvo-mobile-rail-tab.more-section"
				>
					<div className={styles.moreEditor} data-flx="user.konvo-mobile-rail-tab.more-editor">
						<div className={styles.count} data-flx="user.konvo-mobile-rail-tab.more-count">
							<span data-flx="user.konvo-mobile-rail-tab.more-count-text">
								<Trans>
									<b>
										{moreApps.length} of {MOBILE_MORE_MAX_APPS}
									</b>{' '}
									in More
								</Trans>
							</span>
							<CountSegments
								filled={moreApps.length}
								total={MOBILE_MORE_MAX_APPS}
								data-flx="user.konvo-mobile-rail-tab.more-count-segments"
							/>
						</div>
						<div className={styles.rows} data-flx="user.konvo-mobile-rail-tab.more-rows">
							{moreRows.map((row) => {
								const label = i18n._(MOBILE_RAIL_TAB_LABELS[row.id]);
								const unavailable = row.onRail || (!row.on && moreFull);
								return (
									<div
										key={row.id}
										className={clsx(styles.row, !row.on && styles.rowOff, unavailable && styles.rowFull)}
										data-flx="user.konvo-mobile-rail-tab.more-row"
									>
										<span className={styles.grab} aria-hidden="true">
											<DotsSixVerticalIcon size={18} />
										</span>
										<span className={styles.iconTile}>
											<MobileRailTabIcon tab={row.id} size={18} />
										</span>
										<span className={styles.label}>
											{label}
											{unavailable && (
												<small className={styles.labelHint}>
													{i18n._(row.onRail ? ON_THE_RAIL_DESCRIPTOR : MORE_IS_FULL_DESCRIPTOR)}
												</small>
											)}
										</span>
										{row.on && (
											<span className={styles.moveButtons}>
												<button
													type="button"
													className={styles.moveButton}
													disabled={!row.canMoveUp}
													onClick={() => handleMoreMove(row.id, -1)}
													aria-label={i18n._(MOVE_MORE_UP_DESCRIPTOR, {label})}
												>
													<CaretUpIcon size={16} />
												</button>
												<button
													type="button"
													className={styles.moveButton}
													disabled={!row.canMoveDown}
													onClick={() => handleMoreMove(row.id, 1)}
													aria-label={i18n._(MOVE_MORE_DOWN_DESCRIPTOR, {label})}
												>
													<CaretDownIcon size={16} />
												</button>
											</span>
										)}
										<Switch
											compact
											value={row.on}
											disabled={!row.canToggle}
											onChange={() => handleMoreToggle(row.id)}
											ariaLabel={i18n._(SHOW_IN_MORE_DESCRIPTOR, {label})}
											className={styles.switch}
										/>
									</div>
								);
							})}
						</div>
					</div>
				</SettingsSection>
			</SettingsTabContent>
		</SettingsTabContainer>
	);
});

export default KonvoMobileRailSettingsTab;
